/**
 * Admin Authentication Utility
 * Section 6.1 - Client-side authentication management
 */

const AUTH_TOKEN_KEY = 'admin_token';
const AUTH_EXPIRY_KEY = 'admin_token_expiry';

/**
 * Store authentication token
 */
export function setToken(token, expiresIn) {
  localStorage.setItem(AUTH_TOKEN_KEY, token);
  localStorage.setItem(AUTH_EXPIRY_KEY, Date.now() + expiresIn);
}

/**
 * Get stored token if valid
 */
export function getToken() {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  const expiry = localStorage.getItem(AUTH_EXPIRY_KEY);

  if (!token || !expiry) return null;

  // Check if expired
  if (Date.now() > parseInt(expiry)) {
    clearToken();
    return null;
  }

  return token;
}

/**
 * Clear stored token (logout)
 */
export function clearToken() {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_EXPIRY_KEY);
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated() {
  return getToken() !== null;
}

/**
 * Login with password
 */
export async function login(password) {
  try {
    const response = await fetch('/api/admin/auth', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ password })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Login failed');
    }

    setToken(data.token, data.expiresIn);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

/**
 * Logout
 */
export function logout() {
  clearToken();
}

/**
 * Verify current token with server
 */
export async function verifyAuth() {
  const token = getToken();
  if (!token) return false;

  try {
    const response = await fetch('/api/admin/auth', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      clearToken();
      return false;
    }

    return true;
  } catch (error) {
    clearToken();
    return false;
  }
}

/**
 * Get authorization headers for API calls
 */
export function getAuthHeaders() {
  const token = getToken();
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}

const adminAuth = {
  login,
  logout,
  isAuthenticated,
  verifyAuth,
  getToken,
  getAuthHeaders,
  clearToken
};

export default adminAuth;
